const CHAT_LIKES_STORAGE_PREFIX = 'playgrid-chat-liked-seqs:';
const MAX_PERSISTED_CHAT_LIKES = 300;

function storageKey(playerId: string): string {
  return `${CHAT_LIKES_STORAGE_PREFIX}${playerId.trim()}`;
}

function readPersistedChatLikes(
  playerId: string | null | undefined
): Set<string> {
  const trimmed = playerId?.trim() ?? '';
  if (!trimmed) {
    return new Set();
  }

  try {
    const raw = localStorage.getItem(storageKey(trimmed));
    if (!raw) {
      return new Set();
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return new Set();
    }
    return new Set(
      parsed.filter((value): value is string => typeof value === 'string')
    );
  } catch {
    return new Set();
  }
}

function writePersistedChatLikes(
  playerId: string | null | undefined,
  clientSeqs: Set<string>
): void {
  const trimmed = playerId?.trim() ?? '';
  if (!trimmed) {
    return;
  }

  try {
    const values = Array.from(clientSeqs).slice(-MAX_PERSISTED_CHAT_LIKES);
    localStorage.setItem(storageKey(trimmed), JSON.stringify(values));
  } catch {
    // ignore quota / private mode
  }
}

export function isChatLikePersisted(
  playerId: string | null | undefined,
  clientSeq: string | null | undefined
): boolean {
  const seq = clientSeq?.trim() ?? '';
  if (!seq) {
    return false;
  }
  return readPersistedChatLikes(playerId).has(seq);
}

export function rememberChatLike(
  playerId: string | null | undefined,
  clientSeq: string | null | undefined
): void {
  const trimmedPlayerId = playerId?.trim() ?? '';
  const seq = clientSeq?.trim() ?? '';
  if (!trimmedPlayerId || !seq) {
    return;
  }

  const persisted = readPersistedChatLikes(trimmedPlayerId);
  if (persisted.has(seq)) {
    return;
  }
  persisted.add(seq);
  writePersistedChatLikes(trimmedPlayerId, persisted);
}

export function forgetChatLike(
  playerId: string | null | undefined,
  clientSeq: string | null | undefined
): void {
  const trimmedPlayerId = playerId?.trim() ?? '';
  const seq = clientSeq?.trim() ?? '';
  if (!trimmedPlayerId || !seq) {
    return;
  }

  const persisted = readPersistedChatLikes(trimmedPlayerId);
  if (!persisted.delete(seq)) {
    return;
  }
  writePersistedChatLikes(trimmedPlayerId, persisted);
}

export function syncChatLikePersistence(
  playerId: string | null | undefined,
  clientSeq: string | null | undefined,
  likedByMe: boolean
): void {
  if (likedByMe) {
    rememberChatLike(playerId, clientSeq);
    return;
  }
  forgetChatLike(playerId, clientSeq);
}
